// Training — where the team learns the software.
//
// Two sections: written LESSONS and recorded VIDEOS. Content lives in
// src/lib/training-content.ts (typed blocks, no HTML, no API) so publishing
// training is a content edit, not a new endpoint. Read-only by nature, so
// every staff role — including a view-only user — can open it.
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Clock,
  ExternalLink,
  GraduationCap,
  PlayCircle,
  Video as VideoIcon,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { EmptyState } from "../components/EmptyState";
import {
  TRAINING_LESSONS,
  TRAINING_VIDEOS,
  groupByCategory,
  isEmbeddableVideoUrl,
  type LessonBlock,
  type TrainingLesson,
} from "../lib/training-content";

type Tab = "lessons" | "videos";

/* ------------------------------------------------------------------ */
/* Lesson body renderer — typed blocks only, never dangerouslySetInnerHTML */
/* ------------------------------------------------------------------ */
function LessonBody({ blocks }: { blocks: LessonBlock[] }) {
  return (
    <div className="training-body">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "h":
            return <h3 key={i}>{b.text}</h3>;
          case "p":
            return <p key={i}>{b.text}</p>;
          case "bullets":
            return (
              <ul key={i}>
                {b.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            );
          case "steps":
            return (
              <ol key={i}>
                {b.items.map((it, j) => <li key={j}>{it}</li>)}
              </ol>
            );
          case "note":
            return <p key={i} className="training-note">{b.text}</p>;
          default:
            return null;
        }
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A single lesson at /training/:slug                                   */
/* ------------------------------------------------------------------ */
function LessonReader({ lesson }: { lesson: TrainingLesson }) {
  const nav = useNavigate();
  return (
    <Shell title={lesson.title} subtitle={lesson.category}>
      <button className="ghost-button" onClick={() => nav("/training")} style={{ marginBottom: 16 }}>
        <ArrowLeft size={15} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 6 }} />
        All training
      </button>
      <section className="panel training-reader">
        <p className="training-lede">{lesson.summary}</p>
        {lesson.minutes ? (
          <span className="training-meta">
            <Clock size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
            {lesson.minutes} min read
          </span>
        ) : null}
        <LessonBody blocks={lesson.body} />
      </section>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* Video card + inline player                                           */
/* ------------------------------------------------------------------ */
function VideoCard({
  video,
  onPlay,
}: {
  video: { slug: string; title: string; summary: string; duration?: string; url: string };
  onPlay: (slug: string) => void;
}) {
  const embeddable = isEmbeddableVideoUrl(video.url);
  return (
    <article className="training-video-card">
      <div className="training-video-thumb" aria-hidden="true">
        <PlayCircle size={30} />
      </div>
      <div className="training-video-info">
        <strong>{video.title}</strong>
        <span>{video.summary}</span>
        {video.duration ? <span className="training-meta">{video.duration}</span> : null}
      </div>
      {embeddable ? (
        <button className="ghost-button" onClick={() => onPlay(video.slug)}>
          <PlayCircle size={15} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 6 }} />
          Play
        </button>
      ) : (
        <a className="ghost-button" href={video.url} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={15} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 6 }} />
          Watch
        </a>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Training index                                                       */
/* ------------------------------------------------------------------ */
export default function TrainingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>("lessons");
  const [playing, setPlaying] = useState<string | null>(null);

  const lesson = useMemo(
    () => (slug ? TRAINING_LESSONS.find((l) => l.slug === slug) ?? null : null),
    [slug],
  );

  const lessonGroups = useMemo(() => groupByCategory(TRAINING_LESSONS), []);
  const videoGroups = useMemo(() => groupByCategory(TRAINING_VIDEOS), []);

  // A bad/stale /training/:slug shouldn't 404 into the dashboard fallback —
  // show the index instead of a blank screen.
  if (slug && lesson) return <LessonReader lesson={lesson} />;

  // Match on slug AND re-check the allowlist here, not just on the card: if
  // two entries ever shared a slug, clicking the allowed one could select the
  // other and frame a URL that was never allowed.
  const playingVideo = playing
    ? TRAINING_VIDEOS.find((v) => v.slug === playing && isEmbeddableVideoUrl(v.url)) ?? null
    : null;

  return (
    <Shell title="Training" subtitle="Lessons and videos for using this software">
      {slug && !lesson ? (
        <p className="error-text" role="alert" style={{ marginBottom: 14 }}>
          That lesson isn&apos;t published yet. Here&apos;s everything that is.
        </p>
      ) : null}

      <div className="profile-tabs" role="tablist" aria-label="Training sections" style={{ marginBottom: 16 }}>
        <button
          role="tab"
          aria-selected={tab === "lessons"}
          className={tab === "lessons" ? "selected" : undefined}
          onClick={() => setTab("lessons")}
        >
          <BookOpen size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
          Lessons{TRAINING_LESSONS.length ? ` (${TRAINING_LESSONS.length})` : ""}
        </button>
        <button
          role="tab"
          aria-selected={tab === "videos"}
          className={tab === "videos" ? "selected" : undefined}
          onClick={() => setTab("videos")}
        >
          <VideoIcon size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
          Videos{TRAINING_VIDEOS.length ? ` (${TRAINING_VIDEOS.length})` : ""}
        </button>
      </div>

      {/* ---------------- LESSONS ---------------- */}
      {tab === "lessons" && (
        TRAINING_LESSONS.length === 0 ? (
          <section className="panel">
            <EmptyState
              icon={<GraduationCap size={30} aria-hidden="true" />}
              title="No written lessons yet"
              hint="Step-by-step written lessons for every part of the software land here. Nothing has been published yet."
            />
          </section>
        ) : (
          <>
            {lessonGroups.map(([category, items]) => (
              <section className="panel" key={category} style={{ marginBottom: 16 }}>
                <div className="panel-header"><h2>{category}</h2></div>
                <div className="table-list">
                  {items.map((l) => (
                    <button
                      key={l.slug}
                      className="table-row table-row--wide"
                      onClick={() => nav(`/training/${l.slug}`)}
                    >
                      <BookOpen size={18} aria-hidden="true" style={{ flex: "0 0 auto", opacity: 0.7 }} />
                      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                        <strong style={{ display: "block" }}>{l.title}</strong>
                        <span style={{ display: "block", whiteSpace: "normal", overflow: "visible" }}>
                          {l.summary}
                        </span>
                      </div>
                      {l.minutes ? <span style={{ flex: "0 0 auto" }}>{l.minutes} min</span> : null}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </>
        )
      )}

      {/* ---------------- VIDEOS ---------------- */}
      {tab === "videos" && (
        TRAINING_VIDEOS.length === 0 ? (
          <section className="panel">
            <EmptyState
              icon={<VideoIcon size={30} aria-hidden="true" />}
              title="No training videos yet"
              hint="Recorded walkthroughs of the software will show up here as they're published."
            />
          </section>
        ) : (
          <>
            {playingVideo && isEmbeddableVideoUrl(playingVideo.url) ? (
              <section className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-header"><h2>{playingVideo.title}</h2></div>
                <div className="training-player">
                  <iframe
                    src={playingVideo.url}
                    title={playingVideo.title}
                    allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    loading="lazy"
                  />
                </div>
                <button className="ghost-button" onClick={() => setPlaying(null)} style={{ marginTop: 12 }}>
                  Close player
                </button>
              </section>
            ) : null}
            {videoGroups.map(([category, items]) => (
              <section className="panel" key={category} style={{ marginBottom: 16 }}>
                <div className="panel-header"><h2>{category}</h2></div>
                <div className="training-video-grid">
                  {items.map((v) => <VideoCard key={v.slug} video={v} onPlay={setPlaying} />)}
                </div>
              </section>
            ))}
          </>
        )
      )}
    </Shell>
  );
}
